import * as React from 'react';
import TextField from "@mui/material/TextField";
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Grid, Stack, Radio, RadioGroup, FormControlLabel, FormHelperText } from '@mui/material';
import { patchOverrideRequest } from "../../../store/requestOverride/actions"
import { REQUEST_STATUS, REQUEST_STATUS_VALUES } from '../../../constants/_pickerValues';
import { Button } from '@mui/material';
import CustomizedDialogs from '../../atoms/modal/customizedDialogs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import Box from '@mui/material/Box';
import { patchOverrideRequestSelector } from '../../../store/requestOverride/selectors';


export type OverrideRequestUpdateProps = {
    isOpen: boolean,
    handleClose: (refresh?: boolean, requestId?: string) => void;
    tableId: string,
    grantee: string,
    treatmentLevel: string,
    requestId: string,
    requestStatus: string,
    statusDetailMessage: string,
    expiryDate: string,
    dataSteward: string,
    updateActionType: string,
};


const OverrideRequestUpdateStatus = (props: OverrideRequestUpdateProps) => {
    const [expiryType, setExpiryType] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [statusDetailMessage, setStatusDetailMsg] = useState('');
    const [newStatus, setNewStatus] = useState('');
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogDescription, setDialogDescription] = useState('');
    const patchOverrideRequestSuccess = useSelector(patchOverrideRequestSelector);

    const dispatch = useDispatch();

    useEffect(() => {
        if (props.updateActionType === 'cancel') {
            setUpCancelDialog();
        } else if (props.updateActionType === 'reject') {
            setUpRejectDialog();
        } else if (props.updateActionType === 'approve') {
            setUpApproveDialog();
        } else if (props.updateActionType === 'retry') {
            setUpRetryDialog();
        } else if (props.updateActionType === 'skip') {
            setUpSkipDialog();
        }
    }, [props.updateActionType, props.isOpen]);

    const setUpCancelDialog = () => {
        setDialogTitle("Delete Confirmation");
        setDialogDescription(`Are you sure you want to cancel the following override request?`);
        setNewStatus(REQUEST_STATUS_VALUES.cancelled);
    }

    const setUpRejectDialog = () => {
        setNewStatus(REQUEST_STATUS_VALUES.rejected);
        setDialogTitle("Confirm Request Rejection");
        setDialogDescription(`Are you sure you want to reject the the following override request?`);
    }

    const setUpApproveDialog = () => {
        if (props.requestStatus === REQUEST_STATUS.awaiting_review) {
            setNewStatus(REQUEST_STATUS_VALUES.requested);
            setDialogTitle("Confirm Creation of New Override Request");
            setDialogDescription(
                `Are you sure you want to approve the creation of the following override request?
                This will trigger automatic email notification of the Data Steward '${props.dataSteward}'.`
            );
        } else if (props.requestStatus === REQUEST_STATUS.awaiting_approval) {
            setNewStatus(REQUEST_STATUS_VALUES.approved);
            setDialogTitle("Confirm Request Approval");
            setDialogDescription(`Are you sure you want to approve the following override request?`)
            setExpiryType('default');
        };
    }

    const setUpRetryDialog = () => {
        if (props.requestStatus === REQUEST_STATUS.pending_notification) {
            setNewStatus(REQUEST_STATUS_VALUES.requested);
            setDialogTitle("Confirm Retry Notification Automation");
            setDialogDescription(`Are you sure you want to retry notification automation for the following override request?
                This will trigger email notification workflow for Data Steward '${props.dataSteward}'.`)
        } else if (props.requestStatus === REQUEST_STATUS.pending_completion) {
            setNewStatus(REQUEST_STATUS_VALUES.approved);
            setDialogTitle("Confirm Retry Approval Automation");
            setDialogDescription(`Are you sure you want to retry approved completion automation for the following override request?
                This will trigger the completion of the request via the Permission Service.`);
            setExpiryDate(props.expiryDate);
            setExpiryType('selectDate');
        }
    }

    const setUpSkipDialog = () => {
        if (props.requestStatus === REQUEST_STATUS.pending_notification) {
            setNewStatus(REQUEST_STATUS_VALUES.awaiting_approval);
            setDialogTitle("Confirm Skip Notification Automation");
            setDialogDescription(`Are you sure you want to skip the the following override request notification automation?
                This will skip the email notification workflow step and change the request status to 'Awaiting Data Steward Approval'.
                Please only proceed if the Data Steward has been notified manually or if an admin will be approving the request instead.`);
        } else if (props.requestStatus === REQUEST_STATUS.pending_completion) {
            setNewStatus(REQUEST_STATUS_VALUES.completed);
            setDialogTitle("Confirm Skip Completion Automation");
            setDialogDescription(`Are you sure you want to skip the completion automation of the following approved override request? 
                This will change the request status to 'completed'. Please only proceed if the request has been manually fulfilled by an admin.`);
        }
    }

    useEffect(() => {
        if (expiryType === 'default') {
            setExpiryDate(getMaxExpiryDate());
        }
    })

    const handleChangeExpiryType = (event: React.ChangeEvent<HTMLInputElement>) => {
        const eventType = (event.target as HTMLInputElement).value
        setExpiryType(eventType);
    };
    
    const getMaxExpiryDate = () => {
        const currentDate = new Date();
        const maxExpiryDate = new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate());
        const formattedDate = maxExpiryDate.toISOString().split('T')[0];
        return formattedDate;
    }

    const handleOverrideRequestUpdate = () => {
        let payload = {
            status: newStatus, 
            statusDetailMessage: statusDetailMessage != '' ? statusDetailMessage : null, 
            expiryDate: expiryDate != '' && expiryDate != props.expiryDate ? expiryDate : null
        };
        dispatch(patchOverrideRequest(props.requestId, payload));
    }

    useEffect(() => {
        if (patchOverrideRequestSuccess?.isSuccess) {
            props.handleClose(true, props.requestId);
        }
    }, [patchOverrideRequestSuccess]);

    return (
        <CustomizedDialogs
            dialogHeight=''
            modalSize="md"
            key="target"
            modalTitle={dialogTitle}
            isOpen={props.isOpen}
            onClose={props.handleClose}
            footerAction={
                <Stack direction="row" spacing={2}>
                    <Button id="3f9a7d5b-8c2e-4a1f-9d63-eb702fced6af" color="primary"
                            onClick={handleOverrideRequestUpdate} autoFocus>
                        Yes
                    </Button>
                    <Button id="9c7e2a81-4d6f-358b-b2c9-3f8d1e65aeb4" color="primary" onClick={() => {props.handleClose();}}>
                        No
                    </Button>
                </Stack>
            }
        >
            <>{dialogDescription}</>
            <br />
            <br />
            <>Dataset: {props.tableId}</>
            <br />
            <>Treatment Level: {props.treatmentLevel}</>
            <br />
            <>Grantee: {props.grantee}</>
            <br />
            {(props.requestStatus === REQUEST_STATUS.pending_completion || props.requestStatus === REQUEST_STATUS.pending_notification) &&
            <Grid item xs={12}>Error Message: {props.statusDetailMessage}<br /></Grid>
            }
            {newStatus === REQUEST_STATUS_VALUES.approved &&
            <Grid item xs={12}>
            <RadioGroup
                row
                name="expiry-radio-buttons-group"
                onChange={handleChangeExpiryType}
            >
                <FormControlLabel
                    value="default"
                    control={<Radio checked={!expiryType || expiryType === "default"} />} // Set checked to true for "max" value
                    label="Default Expiry (12 Months)"
                />
                <FormControlLabel
                    value="selectDate"
                    control={<Radio checked={expiryType === "selectDate"} />}
                    label="Select Expiry Date"
                />
            </RadioGroup>
            <Box
                component="form"
                sx={{ "& > :not(style)": { m: 1, width: "30ch" } }}
                noValidate
                autoComplete="off"
            >
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="Override Expiry Date"
                        value={expiryDate}
                        disabled={expiryType !== "selectDate"}
                        minDate={new Date()}
                        maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                        onChange={(newValue: Date | null) => {
                            setExpiryDate(newValue!.toISOString().split('T')[0])
                        }}
                        renderInput={(params: any) => {
                            return <TextField
                                {...params} sx={{ width: "100%" }}
                            />;
                        }}
                    />
                    <FormHelperText>
                        {expiryDate < new Date().toISOString().split('T')[0] ? 'Date cannot be in the past. Please select another date.' : ''}
                    </FormHelperText>
                </LocalizationProvider>
            </Box>
        </Grid>}
        {newStatus != REQUEST_STATUS_VALUES.cancelled &&
        <>
        <br />
        <textarea 
            style={{height: '64px', fontSize: '16px', width: '100%'}}
            onChange={(e) => setStatusDetailMsg(e.target.value)} 
        />
        <br />
        <span style={{ fontSize: 'smaller', color: 'grey' }}>Leave a comment (Optional)</span>
        </>
        }
        </CustomizedDialogs>

    )
};

export default OverrideRequestUpdateStatus;